class BdCollectionLoadMore extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onViewportChange = this.onViewportChange.bind(this);
    this._debugClickCount = 0;
    this._loadedPage = null;
    this._listenersBound = false;
    this._viewportMedia = null;
    this._autoLoadEnabled = true;
  }

  connectedCallback() {
    this.button = this.querySelector('[data-bd-load-more-trigger]');
    this.spinner = this.querySelector('.bd-collection-load-more__spinner');
    if (!this._originalParent) {
      this._originalParent = this.parentNode;
      this._originalNextSibling = this.nextSibling;
    }
    this._loadedPage = this.getCurrentPage();
    this.stripPageParamFromBrowserUrl();
    this.positionNearFinderOnFirstView();
    if (!this._listenersBound) {
      this.button?.addEventListener('click', this.onClick);
      this.bindViewportListener();
      this._listenersBound = true;
    }
    this.observeNextPage();

    this.logDebug('init', {
      elementAttributes: this.getElementAttributes(),
      location: window.location.href,
    });
  }

  disconnectedCallback() {
    this._autoLoadObserver?.disconnect();
    this.button?.removeEventListener('click', this.onClick);
    this.unbindViewportListener();
    this._listenersBound = false;
  }

  observeNextPage() {
    this._autoLoadObserver?.disconnect();
    if (!this.isConnected || !this._autoLoadEnabled || this._autoLoadFailed ||
        this.classList.contains('is-complete') || !this.hasAttribute('data-next-url') ||
        !('IntersectionObserver' in window)) return;
    this._autoLoadObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && !this.classList.contains('is-loading')) {
        this._autoLoadObserver.disconnect();
        this.onClick();
      }
    }, { rootMargin: '0px 0px 250px 0px' });
    this._autoLoadObserver.observe(this);
  }

  /* [BD feat] Collection load more — keep mobile/desktop placement in sync on resize */
  bindViewportListener() {
    if (!window.matchMedia) return;
    this._viewportMedia = window.matchMedia('(max-width: 749px)');
    if (this._viewportMedia.addEventListener) {
      this._viewportMedia.addEventListener('change', this.onViewportChange);
    } else if (this._viewportMedia.addListener) {
      this._viewportMedia.addListener(this.onViewportChange);
    }
  }

  unbindViewportListener() {
    if (!this._viewportMedia) return;
    if (this._viewportMedia.removeEventListener) {
      this._viewportMedia.removeEventListener('change', this.onViewportChange);
    } else if (this._viewportMedia.removeListener) {
      this._viewportMedia.removeListener(this.onViewportChange);
    }
    this._viewportMedia = null;
  }

  onViewportChange() {
    // Only re-place while still in the initial "near finder" slot (before first click).
    if (this.dataset.bdLoadMoreFinderMoved !== '1') return;
    this.dataset.bdLoadMoreFinderMoved = '0';
    this.positionNearFinderOnFirstView();
  }
  /* [BD feat] Collection load more — end viewport sync */

  isDebugEnabled() {
    return (
      this.getAttribute('data-bd-debug') === 'true' ||
      window.BD_LOAD_MORE_DEBUG === true ||
      new URLSearchParams(window.location.search).has('bd_load_more_debug')
    );
  }

  getElementAttributes() {
    return {
      sectionId: this.getAttribute('data-section-id'),
      page: this.getAttribute('data-page'),
      totalPages: this.getAttribute('data-total-pages'),
      nextUrl: this.getAttribute('data-next-url'),
      finderMoved: this.getAttribute('data-bd-load-more-finder-moved'),
    };
  }

  describeGrid(grid) {
    if (!grid) return null;

    return {
      tag: grid.tagName.toLowerCase(),
      id: grid.id || null,
      classes: grid.className,
      itemCount: grid.querySelectorAll(':scope > li').length,
      hasLoadMoreTarget: grid.hasAttribute('data-bd-load-more-target'),
      inSpotlight: Boolean(grid.closest('.bd-collection-spotlight, .bd-collection-post-banner-spotlight')),
      productHandles: this.getProductHandlesFromGrid(grid),
    };
  }

  getHandleFromItem(item) {
    const link =
      item.querySelector('[data-product-url]') ||
      item.querySelector('a[href*="/products/"]') ||
      item.querySelector('.card__heading a');

    const href = link?.getAttribute('data-product-url') || link?.getAttribute('href') || '';
    const handleMatch = href.match(/\/products\/([^/?#]+)/);
    return handleMatch ? handleMatch[1] : null;
  }

  getExistingHandles(grid) {
    if (!grid) return new Set();

    return new Set(
      Array.from(grid.querySelectorAll(':scope > li'))
        .map((item) => this.getHandleFromItem(item))
        .filter(Boolean)
    );
  }

  getProductHandlesFromGrid(grid) {
    if (!grid) return [];

    return Array.from(grid.querySelectorAll(':scope > li')).map((item, index) => {
      const href =
        item.querySelector('[data-product-url]')?.getAttribute('data-product-url') ||
        item.querySelector('a[href*="/products/"]')?.getAttribute('href') ||
        null;
      const productId = item.querySelector('[data-product-id]')?.getAttribute('data-product-id') || null;

      return {
        index: index + 1,
        handle: this.getHandleFromItem(item),
        productId,
        href,
      };
    });
  }

  runScrollAnimations(container) {
    if (typeof initializeScrollAnimationTrigger !== 'function' || !container) return;

    try {
      initializeScrollAnimationTrigger(container);
    } catch (error) {
      console.warn('[bd-collection-load-more] Scroll animation init failed', error);
    }
  }

  getFetchedPaginationMeta(fetchedContainer) {
    const fetchedLoadMore = fetchedContainer?.querySelector('bd-collection-load-more');
    if (!fetchedLoadMore) return null;

    return {
      page: fetchedLoadMore.getAttribute('data-page'),
      totalPages: fetchedLoadMore.getAttribute('data-total-pages'),
      nextUrl: fetchedLoadMore.getAttribute('data-next-url'),
    };
  }

  logDebug(phase, payload) {
    const entry = {
      phase,
      timestamp: new Date().toISOString(),
      clickCount: this._debugClickCount,
      ...payload,
    };

    window.__bdLoadMoreLastDebug = entry;
    window.__bdLoadMoreDebugLog = window.__bdLoadMoreDebugLog || [];
    window.__bdLoadMoreDebugLog.push(entry);

    if (this.isDebugEnabled() || phase === 'click-complete' || phase === 'click-error') {
      console.log('[BD_LOAD_MORE_DEBUG]', entry);
    }

    if (phase === 'click-complete' || phase === 'click-error') {
      console.log(
        '[BD_LOAD_MORE_DEBUG] Copy window.__bdLoadMoreLastDebug or window.__bdLoadMoreDebugLog for support'
      );
    }
  }

  getCurrentPage() {
    if (typeof this._loadedPage === 'number' && !Number.isNaN(this._loadedPage)) {
      return this._loadedPage;
    }

    return parseInt(this.getAttribute('data-page') || '1', 10);
  }

  getRequestedPageFromUrl(url) {
    const pageParam = url.searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  }

  getFetchUrl(sectionId) {
    const nextUrl = this.getAttribute('data-next-url');

    if (nextUrl) {
      const url = new URL(nextUrl, window.location.origin);
      url.searchParams.delete('section_id');
      url.searchParams.set('section_id', sectionId);
      return url;
    }

    const nextPage = this.getCurrentPage() + 1;
    return this.buildFetchUrl(nextPage, sectionId);
  }

  getTotalPages() {
    return parseInt(this.getAttribute('data-total-pages') || '0', 10);
  }

  buildPageUrl(page) {
    const url = new URL(this.dataset.collectionUrl || window.location.href, window.location.origin);
    url.searchParams.delete('section_id');
    url.searchParams.delete('page');

    if (page > 1) {
      url.searchParams.set('page', String(page));
    }

    return url;
  }

  stripPageParamFromBrowserUrl() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('page')) return;

    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    const cleanUrl = `${url.pathname}${url.search}`;

    window.location.replace(cleanUrl || url.pathname);
  }

  buildFetchUrl(page, sectionId) {
    const url = this.buildPageUrl(page);
    url.searchParams.set('section_id', sectionId);
    return url;
  }

  getCompleteLabel() {
    return this.getAttribute('data-complete-label') || 'Du hast alle Produkte gesehen';
  }

  getDefaultLabel() {
    return this.button?.getAttribute('data-bd-load-more-default-label') || 'Mehr laden';
  }

  showCompleteState() {
    this._autoLoadObserver?.disconnect();
    this.classList.add('is-complete');
    this.removeAttribute('data-next-url');

    if (!this.button) return;

    this.button.hidden = false;
    this.button.disabled = true;

    const label = this.button.querySelector('.bd-collection-load-more__label');
    if (label) {
      label.textContent = this.getCompleteLabel();
    }
  }

  setPaginationState(page) {
    const totalPages = this.getTotalPages();

    this._loadedPage = page;
    this.setAttribute('data-page', String(page));

    if (totalPages && page >= totalPages) {
      this.showCompleteState();
      return;
    }

    const nextPageUrl = this.buildPageUrl(page + 1);
    this.setAttribute('data-next-url', `${nextPageUrl.pathname}${nextPageUrl.search}`);

    this.classList.remove('is-complete');

    if (this.button) {
      this.button.hidden = false;
      this.button.disabled = false;

      const label = this.button.querySelector('.bd-collection-load-more__label');
      if (label) {
        label.textContent = this.getDefaultLabel();
      }
    }
  }

  syncPaginationFromResponse(fetchedContainer) {
    const fetchedLoadMore = fetchedContainer?.querySelector('bd-collection-load-more');

    if (fetchedLoadMore) {
      const page = parseInt(fetchedLoadMore.getAttribute('data-page') || '1', 10);
      const totalPages = fetchedLoadMore.getAttribute('data-total-pages');
      const nextUrl = fetchedLoadMore.getAttribute('data-next-url');

      this._loadedPage = page;
      this.setAttribute('data-page', String(page));

      if (totalPages) {
        this.setAttribute('data-total-pages', totalPages);
      }

      if (nextUrl) {
        this.setAttribute('data-next-url', nextUrl);
        this.classList.remove('is-complete');

        if (this.button) {
          this.button.hidden = false;
          this.button.disabled = false;

          const label = this.button.querySelector('.bd-collection-load-more__label');
          if (label) {
            label.textContent = this.getDefaultLabel();
          }
        }
      } else {
        this.showCompleteState();
      }

      return true;
    }

    this.setPaginationState(this.getCurrentPage() + 1);
    return false;
  }

  getAppendGrid(container) {
    if (!container) return null;

    const dedicated = container.querySelector('.bd-collection-load-more-grid[data-bd-load-more-target]');
    if (dedicated) return dedicated;

    const targets = Array.from(container.querySelectorAll('[data-bd-load-more-target]'));
    for (let i = targets.length - 1; i >= 0; i--) {
      const target = targets[i];
      if (target.closest('.bd-collection-spotlight, .bd-collection-post-banner-spotlight')) continue;
      return target;
    }

    const continuation = container.querySelector('ul.bd-collection-grid--continuation');
    if (continuation && !continuation.closest('.bd-collection-post-banner-spotlight')) {
      return continuation;
    }

    const spotlightProducts = container.querySelector('.bd-collection-post-banner-spotlight__products');
    const productGrid = container.querySelector('#product-grid');
    if (productGrid && productGrid !== spotlightProducts) {
      return productGrid;
    }

    return null;
  }

  getNewProductsGrid(fetchedContainer, requestedPage) {
    if (requestedPage > 1) {
      const flatGrid = fetchedContainer.querySelector('#product-grid[data-bd-load-more-target]');
      if (flatGrid?.querySelector(':scope > li')) return flatGrid;
    }

    const candidates = [
      ...fetchedContainer.querySelectorAll('.bd-collection-load-more-grid[data-bd-load-more-target]'),
      ...fetchedContainer.querySelectorAll('[data-bd-load-more-target]'),
      fetchedContainer.querySelector('#product-grid'),
    ].filter(Boolean);

    const seen = new Set();
    for (const grid of candidates) {
      if (seen.has(grid)) continue;
      seen.add(grid);
      if (grid.closest('.bd-collection-spotlight, .bd-collection-post-banner-spotlight')) continue;
      if (grid.querySelector(':scope > li')) return grid;
    }

    return null;
  }

  async onClick(event) {
    const sectionId = this.getAttribute('data-section-id');
    if (!sectionId || this.classList.contains('is-loading')) return;
    if (event?.type === 'click') {
      this._autoLoadEnabled = true;
      this._autoLoadFailed = false;
    }
    this._autoLoadObserver?.disconnect();

    this._debugClickCount += 1;

    const currentPage = this.getCurrentPage();
    const totalPages = this.getTotalPages();
    const fetchUrl = this.getFetchUrl(sectionId);
    const requestedPage = this.getRequestedPageFromUrl(fetchUrl);

    const beforeState = {
      elementAttributes: this.getElementAttributes(),
      currentPage,
      totalPages,
      requestedPage,
      fetchUrl: fetchUrl.toString(),
      windowLocation: window.location.href,
    };

    this.logDebug('click-start', beforeState);

    if (totalPages && requestedPage > totalPages) {
      this.logDebug('click-blocked', {
        reason: 'requestedPage exceeds totalPages',
        ...beforeState,
      });
      return;
    }

    if (!this.getAttribute('data-next-url') && totalPages && currentPage >= totalPages) {
      this.logDebug('click-blocked', {
        reason: 'already on last page',
        ...beforeState,
      });
      return;
    }

    this.moveBackToBottom();
    this.setLoading(true);

    const debug = {
      ...beforeState,
      fetchUrl: null,
      responseStatus: null,
      responseHtmlLength: null,
      fetchedPagination: null,
      grids: {
        current: null,
        fetched: null,
      },
      appended: {
        count: 0,
        handles: [],
      },
      afterState: null,
      result: null,
    };

    try {
      debug.fetchUrl = fetchUrl.toString();

      const response = await fetch(fetchUrl.toString());
      debug.responseStatus = response.status;
      if (!response.ok) throw new Error(`Load more failed: ${response.status}`);

      const html = await response.text();
      debug.responseHtmlLength = html.length;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fetchedContainer = doc.getElementById('ProductGridContainer');
      debug.fetchedPagination = this.getFetchedPaginationMeta(fetchedContainer);

      const container = document.getElementById('ProductGridContainer');
      const currentGrid = this.getAppendGrid(container);
      const newGrid = this.getNewProductsGrid(fetchedContainer, requestedPage);

      debug.grids.current = this.describeGrid(currentGrid);
      debug.grids.fetched = this.describeGrid(newGrid);

      const appendResult = this.appendProducts(doc, requestedPage, currentGrid, newGrid);
      debug.appended = appendResult;

      if (appendResult.count === 0 && appendResult.skippedDuplicates === 0) {
        debug.result = 'no-products';
        this.syncPaginationFromResponse(fetchedContainer);
        if (this.classList.contains('is-complete')) {
          debug.result = 'no-products-complete';
        } else if (!this.getAttribute('data-next-url')) {
          if (this.button) {
            this.button.hidden = true;
            this.button.disabled = true;
          }
        }
        debug.afterState = {
          elementAttributes: this.getElementAttributes(),
          windowLocation: window.location.href,
        };
        this.logDebug('click-complete', debug);
        return;
      }

      this.syncPaginationFromResponse(fetchedContainer);
      this.runScrollAnimations(container);

      debug.result = appendResult.skippedDuplicates > 0 && appendResult.count === 0 ? 'skipped-duplicates' : 'success';
      debug.afterState = {
        elementAttributes: this.getElementAttributes(),
        windowLocation: window.location.href,
      };
      this.logDebug('click-complete', debug);
    } catch (error) {
      this._autoLoadFailed = true;
      debug.result = 'error';
      debug.error = {
        message: error?.message || String(error),
        stack: error?.stack || null,
      };
      debug.afterState = {
        elementAttributes: this.getElementAttributes(),
        windowLocation: window.location.href,
      };
      this.logDebug('click-error', debug);
      console.error('[bd-collection-load-more]', error);
    } finally {
      this.setLoading(false);
      this.observeNextPage();
    }
  }

  /* [BD feat] Collection load more — mobile above finder, desktop below spotlight */
  positionNearFinderOnFirstView() {
    if (this._autoLoadEnabled) return;
    if (this.dataset.bdLoadMoreFinderMoved === '1') return;

    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    const spotlight = container.querySelector('.bd-collection-post-banner-spotlight');
    const spotlightGrid = spotlight?.querySelector('.bd-collection-post-banner-spotlight__grid');
    const finderCard = spotlightGrid?.querySelector('.bd-collection-finder-card');
    if (!spotlight || !spotlightGrid || !spotlight.parentNode) return;

    const isMobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;

    if (isMobile && finderCard && finderCard.parentNode) {
      finderCard.parentNode.insertBefore(this, finderCard);
    } else {
      spotlight.parentNode.insertBefore(this, spotlight.nextSibling);
    }
    this.dataset.bdLoadMoreFinderMoved = '1';
  }
  /* [BD feat] Collection load more — end */

  moveBackToBottom() {
    if (this.dataset.bdLoadMoreFinderMoved !== '1') return;
    if (!this._originalParent) return;

    if (this._originalNextSibling && this._originalNextSibling.parentNode === this._originalParent) {
      this._originalParent.insertBefore(this, this._originalNextSibling);
    } else {
      this._originalParent.appendChild(this);
    }

    this.dataset.bdLoadMoreFinderMoved = '0';
  }

  appendProducts(doc, requestedPage, currentGrid, newGrid) {
    const container = document.getElementById('ProductGridContainer');
    const fetchedContainer = doc.getElementById('ProductGridContainer');
    if (!container || !fetchedContainer) {
      return { count: 0, handles: [], reason: 'missing-container' };
    }

    currentGrid = currentGrid || this.getAppendGrid(container);
    newGrid = newGrid || this.getNewProductsGrid(fetchedContainer, requestedPage);

    if (!currentGrid || !newGrid) {
      return {
        count: 0,
        handles: [],
        reason: !currentGrid ? 'missing-current-grid' : 'missing-fetched-grid',
      };
    }

    currentGrid.removeAttribute('hidden');
    currentGrid.classList.remove('hidden');

    const items = newGrid.querySelectorAll(':scope > li');
    const existingHandles = this.getExistingHandles(currentGrid);
    const appendedHandles = [];
    const skippedHandles = [];

    items.forEach((item) => {
      const handle = this.getHandleFromItem(item);
      if (handle && existingHandles.has(handle)) {
        skippedHandles.push(handle);
        return;
      }

      currentGrid.appendChild(document.importNode(item, true));
      if (handle) existingHandles.add(handle);
      appendedHandles.push(handle || null);
    });

    return {
      count: appendedHandles.length,
      handles: appendedHandles.filter(Boolean),
      skippedDuplicates: skippedHandles.length,
      skippedHandles,
      reason: appendedHandles.length ? 'appended' : 'all-duplicates',
    };
  }

  setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);
    this.setAttribute('aria-busy', String(isLoading));
    if (this.button) {
      this.button.disabled = isLoading || this.classList.contains('is-complete');
    }
    this.spinner?.classList.toggle('hidden', !isLoading);
  }
}

if (!customElements.get('bd-collection-load-more')) {
  customElements.define('bd-collection-load-more', BdCollectionLoadMore);
}


